import {useCallback, useEffect} from 'react';
// Import leaf helpers directly (not the utils/helpers barrel) to stay clear of the
// barrel's circular-dependency startup pitfalls.
import {dateHelpers} from '../../utils/helpers/date.helpers';
import {objectHelpers} from '../../utils/helpers/object.helpers';
import {TRANSACTION_PAGE_SEARCH_KEYS} from '../../pages/TransactionsPage/transactionPage.constants';

const {SELECTED_TRANSACTION_ID, SELECTED_TRANSACTION_DATE} = TRANSACTION_PAGE_SEARCH_KEYS;

/**
 * Shared transaction deep-linking for the transactions page tabs (Withless & open banking).
 *
 * Returns `writeSelectedTransaction`, which reflects the opened transaction in the URL query
 * params (preserving any existing params) so the selection survives a refresh and is shareable.
 * It also registers an effect that re-opens the detail panel on load whenever the URL names a
 * transaction that is present in the currently loaded rows.
 *
 * The two tabs differ only in how a row is matched (Withless keys on `expense_id` and needs the
 * expense loaded; open banking keys on the row `id`) and in the "already open" guard — both are
 * injected via `findTransaction` and `isOpen`.
 *
 * @param {Object} params
 * @param {URLSearchParams} params.searchParams
 * @param {Function} params.setSearchParams
 * @param {Array} params.transactions - loaded (grouped) rows; the open effect re-runs when this changes
 * @param {boolean} params.isOpen - a panel is already open, so skip auto-opening
 * @param {Function} params.findTransaction - (transactions, selectedId) => row | undefined
 * @param {Function} params.onOpen - (rowId) => void, opens the details panel for that row
 * @returns {{writeSelectedTransaction: (transactionId: string, createdDate: string) => void}}
 */
export const useTransactionDeepLink = ({
  searchParams,
  setSearchParams,
  transactions,
  isOpen,
  findTransaction,
  onOpen,
}) => {
  const writeSelectedTransaction = useCallback(
    (transactionId, createdDate) => {
      setSearchParams({
        ...objectHelpers.getSearchParams(searchParams),
        [SELECTED_TRANSACTION_ID]: transactionId,
        [SELECTED_TRANSACTION_DATE]: dateHelpers.getDateWithMonth(createdDate, 'YYYY-MM-DD'),
      });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (isOpen) return;
    const selectedTransactionId = searchParams.get(SELECTED_TRANSACTION_ID);
    if (!selectedTransactionId) return;
    const transaction = findTransaction(transactions, selectedTransactionId);
    if (transaction) onOpen(transaction.id);
  }, [transactions]); // eslint-disable-line react-hooks/exhaustive-deps

  return {writeSelectedTransaction};
};
